<template>
    <div class="works d_flex_col center" id="works">
        <div class="section_title_wrapper">
            <h2 class="section_title">Here, take a look at my Works</h2>
        </div>
        
        <!-- Carousel Container -->
        <div class="carousel-container" @mouseenter="pauseCarousel" @mouseleave="resumeCarousel">
            <div class="carousel-track" :style="{ transform: `translateX(-${currentIndex * (itemWidth + 20)}px)` }">
                <div
                    class="project-card"
                    v-for="work in works"
                    :key="work.id"
                    v-bind:class="{ d_none_mobile: !work.mobile }"
                    :style="{ width: itemWidth + 'px' }"
                >
                    <a
                        class="project-link"
                        :href="work.link"
                        target="_blank"
                        rel="noreferrer noopeners"
                    >
                        <!-- Icon-based project -->
                        <div v-if="work.type === 'icon'" class="project-icon">
                            <div class="icon-wrapper">
                                <span v-if="work.icon" class="icon">{{ work.icon }}</span>
                                <img v-else-if="work.image" :src="work.image" :alt="work.title" class="project-logo">
                            </div>
                            <div class="project-info">
                                <h3 class="project-title">{{ work.title }}</h3>
                                <p class="project-period">{{ work.period }}</p>
                                <p class="project-description">{{ work.description }}</p>
                            </div>
                        </div>

                        <!-- Image-based project -->
                        <div v-else class="project-image">
                            <img :src="work.image" :alt="work.title" :title="work.title" />
                            <div class="project-overlay">
                                <h3 class="project-title">{{ work.title }}</h3>
                                <p class="project-period">{{ work.period }}</p>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
            
            <!-- Carousel Navigation Dots -->
            <div class="carousel-dots">
                <button
                    v-for="(dot, index) in visibleDots"
                    :key="index"
                    class="dot"
                    :class="{ active: currentDotIndex === index }"
                    @click="goToSlide(index)"
                ></button>
            </div>
        </div>
    </div>
</template>

<script>
import { Intersection_observer } from "@/utils/observer";
import { works } from "@/utils/works";

export default {
    props: ["CURRENT_SECTION"],
    data() {
        return {
            works,
            currentIndex: 0,
            itemWidth: 350, // Width of each project card
            visibleItems: 3, // Number of items visible at once (will be updated based on screen size)
            autoScrollInterval: null,
            isPaused: false,
            currentDotIndex: 0
        };
    },
    computed: {
        totalItems() {
            return this.works.length;
        },
        maxIndex() {
            return Math.max(0, this.totalItems - this.visibleItems);
        },
        visibleDots() {
            return Math.ceil(this.totalItems / this.visibleItems);
        }
    },
    mounted() {
        Intersection_observer(this.handleInteract, this.$el);
        this.updateVisibleItems();
        this.startAutoScroll();
        this.updateItemWidth();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        this.stopAutoScroll();
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        updateVisibleItems() {
            const screenWidth = window.innerWidth;
            console.log('Screen width:', screenWidth); // Debug log
            if (screenWidth <= 768) {
                // Mobile screens
                this.visibleItems = 1;
                console.log('Set to mobile: 1 item'); // Debug log
            } else if (screenWidth <= 1024) {
                // iPad screens
                this.visibleItems = 2;
                console.log('Set to iPad: 2 items'); // Debug log
            } else {
                // Desktop screens
                this.visibleItems = 3;
                console.log('Set to desktop: 3 items'); // Debug log
            }
            console.log('Visible items set to:', this.visibleItems); // Debug log
            console.log('Current index before reset:', this.currentIndex); // Debug log
            console.log('Max index:', this.maxIndex); // Debug log
            // Reset current index to prevent out of bounds
            this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
            console.log('Current index after reset:', this.currentIndex); // Debug log
            this.updateDotIndex();
        },
        
        handleResize() {
            this.updateVisibleItems();
            this.updateItemWidth();
        },
        
        updateItemWidth() {
            const containerElement = this.$el.querySelector('.carousel-container');
            const containerWidth = containerElement ? containerElement.offsetWidth : 1200;
            this.itemWidth = Math.floor((containerWidth - (this.visibleItems - 1) * 20) / this.visibleItems); // Account for gaps properly
        },
        
        startAutoScroll() {
            this.autoScrollInterval = setInterval(() => {
                if (!this.isPaused) {
                    this.nextSlide();
                }
            }, 4000); // Auto-scroll every 4 seconds
        },
        
        stopAutoScroll() {
            if (this.autoScrollInterval) {
                clearInterval(this.autoScrollInterval);
                this.autoScrollInterval = null;
            }
        },
        
        pauseCarousel() {
            this.isPaused = true;
        },
        
        resumeCarousel() {
            this.isPaused = false;
        },
        
        nextSlide() {
            if (this.currentIndex < this.maxIndex) {
                this.currentIndex += this.visibleItems; // Move by visible items count
            } else {
                this.currentIndex = 0; // Loop back to start
            }
            this.updateDotIndex();
        },
        
        prevSlide() {
            if (this.currentIndex > 0) {
                this.currentIndex -= this.visibleItems; // Move by visible items count
            } else {
                this.currentIndex = this.maxIndex; // Loop to end
            }
            this.updateDotIndex();
        },
        
        goToSlide(index) {
            this.currentIndex = index * this.visibleItems;
            this.currentDotIndex = index;
        },
        
        updateDotIndex() {
            this.currentDotIndex = Math.floor(this.currentIndex / this.visibleItems);
        },

        async handleInteract(entries, observer) {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.$store.dispatch("SET_CURRENT_SECTION", "works");
                }
            });
        }
    },

    watch: {
        CURRENT_SECTION: function(newSection, oldSection) {},
    },
};
</script>

<style lang="scss">
.works {
    margin: 2rem 0;
    padding: 2rem 0;

    .section_title {
        color: $secondary;
        text-align: center;
        font-size: 40px;
        margin-bottom: 3rem;
    }

    .carousel-container {
        width: 90%;
        max-width: 1200px;
        margin: 0 auto;
        position: relative;
        overflow: hidden;
        border-radius: 12px;
        box-sizing: border-box;
    }

    .carousel-track {
        display: flex;
        transition: transform 0.5s ease-in-out;
        gap: 20px;
    }

    .project-card {
        flex-shrink: 0;
        height: 400px;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;

        &:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }
    }

    .project-link {
        display: flex;
        height: 100%;
        text-decoration: none;
        color: inherit;
        flex-direction: column;
    }

    // Icon-based project cards
    .project-icon {
        height: 100%;
        background: linear-gradient(135deg, #20b2aa 0%, #008b8b 100%);
        display: flex;
        flex-direction: column;
        padding: 2rem;
        color: white;
        position: relative;
        overflow: hidden;
        flex: 1;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.1);
            z-index: 1;
        }

        .icon-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 80px;
            margin-bottom: 1.5rem;
            position: relative;
            z-index: 2;

            .icon {
                font-size: 3rem;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
            }

            .project-logo {
                width: 60px;
                height: 60px;
                object-fit: contain;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
            }
        }

        .project-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            position: relative;
            z-index: 2;
            height: 100%;

            .project-title {
                font-size: 1.4rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
                line-height: 1.3;
                flex-shrink: 0;
            }

            .project-period {
                font-size: 0.9rem;
                opacity: 0.8;
                margin-bottom: 1rem;
                font-weight: 500;
                flex-shrink: 0;
            }

            .project-description {
                font-size: 0.95rem;
                line-height: 1.5;
                opacity: 0.9;
                margin-bottom: 1rem;
                flex: 1;
                max-height: 100px;
                overflow-y: auto;
                padding-right: 0.5rem;
                
                /* Custom scrollbar */
                &::-webkit-scrollbar {
                    width: 4px;
                }
                
                &::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 2px;
                }
                
                &::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 2px;
                }
                
                &::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.5);
                }
            }
        }
    }

    // Image-based project cards
    .project-image {
        height: 100%;
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, #e0f7fa 0%, #b2dfdb 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;

        img {
            max-width: 80%;
            max-height: 60%;
            width: auto;
            height: auto;
            object-fit: contain;
            transition: transform 0.3s ease;
            filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
        }

        .project-overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
            padding: 2rem 1.5rem 1.5rem;
            color: white;
            transform: translateY(100%);
            transition: transform 0.3s ease;

            .project-title {
                font-size: 1.3rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
            }

            .project-period {
                font-size: 0.9rem;
                opacity: 0.8;
            }
        }

        &:hover {
            img {
                transform: scale(1.1);
            }

            .project-overlay {
                transform: translateY(0);
            }
        }
    }

    // Carousel navigation dots
    .carousel-dots {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        margin-top: 2rem;

        .dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: none;
            background: rgba(0, 0, 0, 0.3);
            cursor: pointer;
            transition: all 0.3s ease;

            &.active {
                background: $secondary;
                transform: scale(1.2);
            }

            &:hover {
                background: rgba(0, 0, 0, 0.5);
            }
        }
    }
}

// Responsive Design
@media screen and (max-width: 1024px) {
    .works {
        .carousel-container {
            width: 95%;
        }

        .project-card {
            height: 350px;
        }

        .project-icon {
            padding: 1.5rem;

            .icon-wrapper .icon {
                font-size: 2.5rem;
            }

            .project-info {
                .project-title {
                    font-size: 1.2rem;
                }

                .project-description {
                    font-size: 0.9rem;
                }
            }
        }
    }
}

// iPad screens (769px - 1024px)
@media screen and (min-width: 769px) and (max-width: 1024px) {
    .works {
        .carousel-container {
            width: 95%;
            padding: 0 0.5rem;
            box-sizing: border-box;
        }

        .project-card {
            height: 350px;
        }

        .project-icon {
            padding: 1.5rem;

            .icon-wrapper {
                height: 70px;
                margin-bottom: 1.2rem;

                .icon {
                    font-size: 2.5rem;
                }
            }

            .project-info {
                .project-title {
                    font-size: 1.2rem;
                }

                .project-description {
                    font-size: 0.9rem;
                }
            }
        }
    }
}

@media screen and (max-width: 768px) {
    .works {
        margin: 1rem 0;
        padding: 1rem 0;

        .section_title {
            font-size: 30px !important;
            margin: 20px 0 2rem;
        }

        .carousel-container {
            width: 100%;
            padding: 0 1rem;
            box-sizing: border-box;
        }

        .project-card {
            height: 300px;
        }

        .project-icon {
            padding: 1rem;

            .icon-wrapper {
                height: 60px;
                margin-bottom: 1rem;

                .icon {
                    font-size: 2rem;
                }
            }

            .project-info {
                .project-title {
                    font-size: 1.1rem;
                }

                .project-description {
                    font-size: 0.85rem;
                    margin-bottom: 1rem;
                }

                .skills-tags {
                    .skill-tag {
                        font-size: 0.75rem;
                        padding: 0.2rem 0.6rem;
                    }
                }
            }
        }

        .project-image {
            padding: 1rem;

            img {
                max-width: 70%;
                max-height: 50%;
            }

            .project-overlay {
                padding: 1.5rem 1rem 1rem;

                .project-title {
                    font-size: 1.1rem;
                }

                .project-period {
                    font-size: 0.8rem;
                }
            }
        }
    }
}

@media screen and (max-width: 480px) {
    .works {
        .project-card {
            height: 280px;
        }

        .project-icon {
            padding: 0.8rem;

            .project-info {
                .project-title {
                    font-size: 1rem;
                }

                .project-description {
                    font-size: 0.8rem;
                }
            }
        }

        .project-image {
            padding: 0.8rem;

            img {
                max-width: 60%;
                max-height: 40%;
            }
        }
    }
}
</style>
